import React, { Fragment, useEffect, useRef, useState } from 'react';
import { arrayOf, object, string, func } from 'prop-types';
import { nanoid } from 'nanoid';

import Dropdown from '../dropdown';

const sortKeys = (obj) =>
  Object.keys(obj)
    .sort()
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: obj[key]
      }),
      {}
    );

function List({
  results,
  states,
  countries,
  selectStateLabel,
  selectCountryLabel,
  stateQs,
  setStateQs,
  countryQs,
  setCountryQs,
  showAllLabel,
  otherStateLabel,
  selectTermLabel,
  termQs,
  setTermQs,
  terms
}) {
  const listId = useRef(`id-${nanoid()}`);
  const unitedStatesValue = 'United States';
  const [countriesState, setCountriesState] = useState(countries);
  const [statesState, setStatesState] = useState(states);

  const orderedTerms = terms;

  const unitedStates = countries.find(
    (country) => country.value === unitedStatesValue
  );
  const orderedCountries = countries.filter(
    (country) => country.value !== unitedStatesValue
  );
  orderedCountries.splice(1, 0, unitedStates);
  const countryName = countries.find(
    (country) => country.value === countryQs
  )?.label;
  const allStates = sortKeys(
    results
      ?.filter(({ Term }) => Term === termQs)
      ?.filter(({ Country }) => Country === countryQs)
      ?.reduce((acc, current) => {
        const state = current.State || otherStateLabel;
        if (!acc[state]) {
          acc[state] = [];
        }
        acc[state].push(current);
        return acc;
      }, {})
  );

  useEffect(() => {
    if (countriesState.length === 0) {
      setCountriesState(countries);
    }
  }, [countriesState, countries]);

  useEffect(() => {
    if (statesState.length === 0) {
      setStatesState(states);
    }
  }, [statesState, states]);

  return (
    <>
      <div className="deans-list__dropdowns-container">
        {terms?.length > 1 && (
          <Dropdown
            initialValue={termQs}
            id={`${listId}-dropdown`}
            onSelect={(terms) => {
              setTermQs(terms);
              setCountriesState([]);
              setCountryQs('');
              setStateQs('');
              setStatesState([]);
            }}
            options={orderedTerms}
            visibleLabel={selectTermLabel}
          />
        )}
        {countriesState?.length > 1 && (
          <Dropdown
            initialValue={countryQs}
            id={`${listId}-dropdown`}
            onSelect={(country) => {
              setCountryQs(country);
              setStateQs('');
              setStatesState([]);
            }}
            options={orderedCountries}
            visibleLabel={selectCountryLabel}
          />
        )}
        {statesState?.length > 2 && (
          <Dropdown
            initialValue={stateQs}
            id={`${listId}-dropdown`}
            onSelect={setStateQs}
            options={states}
            visibleLabel={selectStateLabel}
          />
        )}
      </div>
      {results?.length > 0 && (
        <>
          {stateQs === showAllLabel || !stateQs || !termQs ? (
            Object.entries(allStates).map(([state, list]) => (
              <Fragment key={state}>
                <h2 className="deans-list__subheading">
                  {states?.length > 2 ? state : countryName}
                </h2>
                <ul className="deans-list__list">
                  {list?.map((item) => (
                    <li key={item.Name}>{item.Name}</li>
                  ))}
                </ul>
              </Fragment>
            ))
          ) : (
            <>
              <h2 className="deans-list__subheading">
                {stateQs &&
                  states.find((state) => state.value === stateQs)?.label}
              </h2>
              <ul className="deans-list__list">
                {results
                  ?.filter(({ State, Country, Term }) => {
                    const state = State || otherStateLabel;
                    return (
                      state === stateQs &&
                      countryQs === Country &&
                      Term === termQs
                    );
                  })
                  ?.map((result) => (
                    <li key={results.Name}>{result.Name}</li>
                  ))}
              </ul>
            </>
          )}
        </>
      )}
    </>
  );
}

List.propTypes = {
  results: arrayOf(object),
  countries: arrayOf(object),
  terms: arrayOf(object),
  states: arrayOf(object),
  selectStateLabel: string.isRequired,
  selectCountryLabel: string.isRequired,
  showAllLabel: string,
  stateQs: string.isRequired,
  countryQs: string.isRequired,
  otherStateLabel: string.isRequired,
  setStateQs: func.isRequired,
  setCountryQs: func.isRequired
};

export default React.memo(List);
